// The files on a case and the handover inspection, beside the checklist
// (db/migrations/2400 and 2402).
//
// Files: the resignation or hearing finding uploaded when the case was opened,
// and anything added since. A case that should carry one and does not says so
// in amber, because the sign off relies on it.
//
// Step 4: the driver's newest Part C inspection. Once attached it is the
// handover inspection, with what the driver reported and the photos copied out
// of Typeform. While the step is open it shows the newest one so far, and the
// step's owner or a signatory can use it at once instead of waiting for the
// nightly check. Every rule is in Postgres; this only shows what comes back.
import { useCallback, useEffect, useRef, useState } from 'react'
import { Camera, ExternalLink, FileText, Paperclip, Upload } from 'lucide-react'
import {
  useAddOffboardingDocument, useAttachOffboardingInspection, useGetOffboardingEvidence, useOpenOffboardingFile,
  useSignOffboardingUpload,
} from '../../hooks/backend/offboarding'
import { Button } from '../../lib/shadcn/button'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { writeErrorMessage } from '../../utils/writeError'
import { formatWhen } from '../../utils/offboarding'
import { DOCUMENT_ACCEPT, checkDocument, uploadSigned, type Signed } from '../../utils/signedUpload'

type Summary = {
  id: string; inspected_at: string; day: string; leg_label: string; registration: string | null
  odometer: number | null; fuel_level: string | null; flagged: { question: string; answer: string }[]
  photo_count: number; photos_copied: number
}
type Evidence = {
  ok: boolean
  documents: { id: string; kind: string; label: string | null; filename: string | null; path: string; uploaded_by: string | null; created_at: string }[]
  required_kind: 'resignation' | 'hearing_outcome' | null
  has_required: boolean
  can_add: boolean
  inspection: {
    attached: Summary | null; attached_by: string | null; attached_at: string | null; latest: Summary | null
    photos: { label: string; path: string }[]; step_status: string; can_attach: boolean; rule: string
  }
}

const REQUIRED_TEXT = {
  resignation: 'No resignation is attached yet. Upload the screenshot of the WhatsApp message or email, or a photo or PDF of the letter.',
  hearing_outcome: 'No hearing finding is attached yet. Upload a photo, screenshot or PDF of the signed outcome.',
}

export function CaseEvidence({ caseId, driverId, version, onChanged }: {
  caseId: string; driverId: string | null; version: string; onChanged: () => void
}) {
  const q = useGetOffboardingEvidence<Evidence>()
  const load = q.trigger
  const refresh = useCallback(() => {
    void load({ case_id: caseId }, { skipCache: true }).catch(() => undefined)
  }, [load, caseId])
  useEffect(() => { refresh() }, [refresh, version])

  const openFile = useOpenOffboardingFile<{ url: string }>()
  const sign = useSignOffboardingUpload<Signed>()
  const add = useAddOffboardingDocument()
  const attach = useAttachOffboardingInspection()
  const [kind, setKind] = useState('')
  const [busy, setBusy] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  const e = q.data
  if (!e) return null
  const ins = e.inspection
  const shown = ins.attached ?? ins.latest

  async function open(path: string) {
    try {
      const r = await openFile.trigger({ path }).result
      if (r?.url) window.open(r.url, '_blank', 'noopener')
    } catch (err) {
      toast.error(writeErrorMessage(err))
    }
  }

  async function upload(file: File) {
    const bad = checkDocument(file)
    if (bad) { toast.error(bad); return }
    if (!driverId) { toast.error('This case has no driver record, so a file cannot be filed against it.'); return }
    setBusy(true)
    try {
      const path = await uploadSigned(await sign.trigger({ driver_id: driverId, filename: file.name }, { skipCache: true }), file)
      await add.trigger({ case_id: caseId, kind: kind || e!.required_kind || 'exit_document', path, filename: file.name }).result
      toast.success('Document added to the case.')
      setKind('')
      refresh()
      onChanged()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  async function useInspection() {
    setBusy(true)
    try {
      await attach.trigger({ case_id: caseId }).result
      toast.success('Step 4 done with the last Part C inspection. The photos are being copied to the case.')
      refresh()
      onChanged()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid gap-3 md:grid-cols-2">
      <section className="space-y-2 rounded-lg border border-border p-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Paperclip className="h-4 w-4 text-muted-foreground" /> Exit documents
        </h3>
        {!e.has_required && e.required_kind && (
          <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
            {REQUIRED_TEXT[e.required_kind]}
          </p>
        )}
        {e.documents.length === 0 ? (
          <p className="text-xs text-muted-foreground">No files on this case.</p>
        ) : (
          <ul className="space-y-1.5">
            {e.documents.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-2 text-sm">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <button type="button" className="min-w-0 truncate text-left font-medium text-[hsl(var(--brand))] hover:underline" onClick={() => void open(d.path)}>
                  {d.label ?? 'Document'}
                </button>
                <span className="min-w-0 truncate text-xs text-muted-foreground">{d.filename} · {d.uploaded_by ?? 'unknown'}, {formatWhen(d.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
        {e.can_add && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <NativeSelect value={kind} onChange={(ev) => setKind(ev.target.value)} className="h-8 w-auto text-xs" aria-label="What the file is">
              <option value="">{e.required_kind === 'hearing_outcome' ? 'The hearing finding' : e.required_kind === 'resignation' ? 'The resignation' : 'Exit document'}</option>
              {e.required_kind !== 'resignation' && <option value="resignation">The resignation</option>}
              {e.required_kind !== 'hearing_outcome' && <option value="hearing_outcome">The hearing finding</option>}
              {e.required_kind !== null && <option value="exit_document">Another exit document</option>}
            </NativeSelect>
            <input ref={input} type="file" accept={DOCUMENT_ACCEPT} className="hidden"
              onChange={(ev) => { const f = ev.target.files?.[0]; if (f) void upload(f) }} />
            <Button size="sm" variant="outline" disabled={busy} onClick={() => input.current?.click()}>
              {busy ? <Spinner className="mr-1 h-3 w-3" /> : <Upload className="mr-1 h-3.5 w-3.5" />} Add a file
            </Button>
          </div>
        )}
      </section>

      <section className="space-y-2 rounded-lg border border-border p-3">
        <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-foreground">
          <Camera className="h-4 w-4 text-muted-foreground" /> Step 4: handover inspection
          {ins.attached ? <Chip tone="green">From Part C</Chip> : ins.step_status === 'open' ? <Chip tone="amber">Waiting</Chip> : null}
        </h3>
        {!shown ? (
          <p className="text-xs text-muted-foreground">This driver has no Part C inspection on record. Inspect the car with them and tick step 4 in the checklist.</p>
        ) : (
          <div className="space-y-1.5 text-sm">
            <p className="text-xs text-muted-foreground">
              {ins.attached
                ? `Used as the handover inspection by ${ins.attached_by ?? 'unknown'}${ins.attached_at ? `, ${formatWhen(ins.attached_at)}` : ''}.`
                : 'The newest Part C inspection so far. It is not on the case yet.'}
            </p>
            <p className="text-foreground">
              <span className="font-medium">{shown.registration ?? 'Car not stated'}</span>, {shown.leg_label.toLowerCase()}, {formatWhen(shown.inspected_at)}
            </p>
            <p className="text-xs text-muted-foreground">
              Odometer {shown.odometer != null ? `${shown.odometer.toLocaleString('en-ZA')} km` : 'not given'} · fuel {shown.fuel_level ?? 'not given'} · {shown.photo_count} photos
            </p>
            {shown.flagged.length === 0 ? (
              <p className="text-xs text-emerald-700 dark:text-emerald-300">Nothing was reported damaged, missing or not working.</p>
            ) : (
              <ul className="space-y-0.5 text-xs">
                {shown.flagged.map((f, i) => (
                  <li key={i} className="text-foreground"><span className="text-muted-foreground">{f.question}</span> {f.answer}</li>
                ))}
              </ul>
            )}
            {ins.attached && ins.photos.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {ins.photos.map((p) => (
                  <Button key={p.path} size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => void open(p.path)}>
                    {p.label} <ExternalLink className="ml-1 h-3 w-3" />
                  </Button>
                ))}
              </div>
            )}
            {ins.attached && ins.photos.length < shown.photo_count && (
              <p className="text-[11px] text-muted-foreground">
                {ins.photos.length} of {shown.photo_count} photos copied from Typeform so far. Refresh in a minute.
              </p>
            )}
          </div>
        )}
        {ins.can_attach && (
          <Button size="sm" variant="brand" disabled={busy} onClick={() => void useInspection()}>
            {busy && <Spinner className="mr-1 h-3 w-3" />} Use this inspection for step 4
          </Button>
        )}
        {ins.step_status === 'open' && <p className="text-[11px] text-muted-foreground">{ins.rule}</p>}
      </section>
    </div>
  )
}
